// frontend/src/components/search/SearchBar.js
import React, { useState, useEffect, useRef } from "react";
import { Search, X, Clock, Play, List, Folder, Newspaper, Trophy, LayoutGrid, HelpCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import apiService from "../../services/apiService";
import ContractBadge from "../common/ContractBadge";
import LanguageBadge from "../common/LanguageBadge";

const SearchResult = ({ item, onClose }) => {
  const { t } = useLanguage();
  const { hasAccess } = useAuth();

  // Helper function to clean HTML from text
  const cleanHtml = (text) => {
    if (typeof text === 'string') {
      return text.replace(/<br\s*\/?>/gi, ' ');
    }
    if (typeof text === 'object' && text !== null) {
      // If text is an object (multilingual), return as is for t() to handle
      return text;
    }
    return text || '';
  };

  const getIcon = () => {
    switch (item.type) {
      case "category":
        return <Folder className="w-4 h-4 text-[#00A3E0]" />;
      case "video":
        return <Play className="w-4 h-4 text-[#00A3E0]" />;
      case "playlist":
        return <List className="w-4 h-4 text-[#00A3E0]" />;
      case "quiz":
        return <Trophy className="w-4 h-4 text-[#00A3E0]" />;
      case "article":
        return <Newspaper className="w-4 h-4 text-[#00A3E0]" />;
      default:
        return null;
    }
  };

  const getImageUrl = () => {
    switch (item.type) {
      case "category":
        return item.imageUrl;
      case "video":
      case "playlist":
        return item.thumbnailUrl;
      case "quiz":
        return item.imageUrl;
      case "article":
        // Articles have full URLs already
        return item.imageUrl?.startsWith('http')
          ? item.imageUrl
          : `https://www.burnair.cloud/images/news/${item.imageUrl}`;
      default:
        return null;
    }
  };

  const getLink = () => {
    // For articles with URL type, return the external URL
    if (item.type === "article" && item.news_type === "url" && item.urlDE) {
      return item.urlDE;
    }
    return `/${item.type}/${item.slug}`;
  };

  // Use backend's hasAccess if available, otherwise fallback to auth check
  const canAccess = item.hasAccess !== undefined ? item.hasAccess : hasAccess(item.contract);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const isExternalLink = item.type === "article" && item.news_type === "url" && item.urlDE;

  const linkClassName = `flex items-center p-3 hover:bg-gray-50 transition-colors ${
    !canAccess ? "opacity-60 cursor-not-allowed" : ""
  }`;

  const content = (
    <>
      <div className="flex-shrink-0 w-20 h-[2.8125rem] bg-gray-100 rounded overflow-hidden">
        <img
          src={getImageUrl()}
          alt={t(cleanHtml(item.title))}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      </div>

      <div className="ml-3 flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {getIcon()}
            <span className="text-xs text-gray-500 capitalize">
              {t({
                en: item.type,
                de:
                  item.type === "category"
                    ? "Kategorie"
                    : item.type === "video"
                    ? "Video"
                    : item.type === "quiz"
                    ? "Quiz"
                    : item.type === "article"
                    ? "Artikel"
                    : "Wiedergabeliste",
              })}
            </span>
          </div>
          <ContractBadge contract={item.contract} />
        </div>

        <h4 className="text-sm font-medium text-[#002855] truncate">
          {t(cleanHtml(item.title))}
        </h4>

        {item.description && (
          <p className="text-xs text-gray-500 truncate mt-1">
            {t(cleanHtml(item.description))}
          </p>
        )}

        <div className="flex items-center mt-1 space-x-2">
          {item.type === "video" && item.language && (
            <LanguageBadge language={item.language} />
          )}
          {item.duration && (
            <div className="flex items-center text-xs text-gray-400">
              <Clock className="w-3 h-3 mr-1" />
              {formatDuration(item.duration)}
            </div>
          )}
        </div>
      </div>
    </>
  );

  // For external links, use a regular anchor tag
  if (isExternalLink && canAccess) {
    return (
      <a
        href={item.urlDE}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClose}
        className={linkClassName}
      >
        {content}
      </a>
    );
  }

  // For internal links, use React Router Link
  return (
    <Link
      to={canAccess ? getLink() : "#"}
      onClick={canAccess ? onClose : undefined}
      className={linkClassName}
    >
      {content}
    </Link>
  );
};

const SearchBar = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [showHelp, setShowHelp] = useState(false);
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const resultsRef = useRef(null);

  // Detect prefix shortcuts in search term
  const detectPrefix = (input) => {
    const prefixes = {
      'article:': 'articles', 'a:': 'articles', 'artikel:': 'articles',
      'video:': 'videos', 'v:': 'videos',
      'playlist:': 'playlists', 'p:': 'playlists', 'wiedergabeliste:': 'playlists',
      'quiz:': 'quizzes', 'q:': 'quizzes',
      'category:': 'categories', 'c:': 'categories', 'kategorie:': 'categories'
    };

    const lowerInput = input.toLowerCase();
    for (const [prefix, type] of Object.entries(prefixes)) {
      if (lowerInput.startsWith(prefix)) {
        return {
          type,
          query: input.slice(prefix.length).trim()
        };
      }
    }

    return { type: 'all', query: input };
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim().length >= 2) {
        performSearch();
      } else {
        setSearchResults(null);
        setShowResults(false);
        setTypeFilter("all");
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, language, typeFilter]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        resultsRef.current &&
        !resultsRef.current.contains(event.target)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const performSearch = async () => {
    if (!searchTerm.trim() || searchTerm.trim().length < 2) return;

    setIsLoading(true);
    try {
      // Detect prefix shortcuts
      const { type: detectedType, query } = detectPrefix(searchTerm.trim());

      // Use detected type from prefix, or current filter
      const searchType = detectedType !== 'all' ? detectedType : typeFilter;
      const searchQuery = detectedType !== 'all' ? query : searchTerm.trim();

      // Update type filter if prefix detected
      if (detectedType !== 'all' && detectedType !== typeFilter) {
        setTypeFilter(detectedType);
      }

      const response = await apiService.search(searchQuery, language, searchType);
      if (response.success) {
        setSearchResults(response.data);
        setShowResults(true);
      }
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSearchResults(null);
    setShowResults(false);
    setTypeFilter("all");
  };

  const handleFilterChange = (newType) => {
    setTypeFilter(newType);
    if (searchTerm.trim().length >= 2) {
      // Trigger search with new filter
      performSearch();
    }
  };

  const handleViewAll = () => {
    const { query } = detectPrefix(searchTerm.trim());
    navigate(`/search?q=${encodeURIComponent(query)}&type=${typeFilter}`);
    closeResults();
  };

  const closeResults = () => {
    setShowResults(false);
  };

  const getTotalResults = () => {
    if (!searchResults) return 0;
    return (
      searchResults.categories.length +
      searchResults.videos.length +
      searchResults.playlists.length +
      (searchResults.quizzes?.length || 0) +
      (searchResults.articles?.length || 0)
    );
  };

  return (
    <div className="relative max-w-md mx-auto">
      <div ref={searchRef} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => searchResults && setShowResults(true)}
            placeholder={t({
              en: "Search videos, playlists, quizzes...",
              de: "Videos, Wiedergabelisten, Quiz suchen...",
            })}
            className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A3E0] focus:border-[#00A3E0] bg-white shadow-sm"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#00A3E0]"></div>
          </div>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && searchResults && (
        <div
          ref={resultsRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
        >
          {getTotalResults() > 0 ? (
            <div>
              {/* Filter Chips */}
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                  {[
                    { id: 'all', label: t({ en: 'All', de: 'Alle' }), icon: <LayoutGrid className="w-3 h-3" /> },
                    { id: 'videos', label: t({ en: 'Videos', de: 'Videos' }), icon: <Play className="w-3 h-3" /> },
                    { id: 'playlists', label: t({ en: 'Playlists', de: 'Wiedergabelisten' }), icon: <List className="w-3 h-3" /> },
                    { id: 'quizzes', label: t({ en: 'Quizzes', de: 'Quiz' }), icon: <Trophy className="w-3 h-3" /> },
                    { id: 'articles', label: t({ en: 'Articles', de: 'Artikel' }), icon: <Newspaper className="w-3 h-3" /> },
                    { id: 'categories', label: t({ en: 'Categories', de: 'Kategorien' }), icon: <Folder className="w-3 h-3" /> }
                  ]
                    .map(filterType => ({
                      ...filterType,
                      count: filterType.id === 'all' ? getTotalResults() : searchResults[filterType.id]?.length || 0
                    }))
                    .filter(filterType => {
                      // Hide chips with zero results, except 'all' and currently active filter
                      if (filterType.id === 'all') return true;
                      if (filterType.id === typeFilter) return true;
                      return filterType.count > 0;
                    })
                    .sort((a, b) => {
                      // Always keep 'all' first
                      if (a.id === 'all') return -1;
                      if (b.id === 'all') return 1;
                      // Sort by count descending
                      return b.count - a.count;
                    })
                    .map(filterType => (
                      <button
                        key={filterType.id}
                        onClick={() => handleFilterChange(filterType.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                          typeFilter === filterType.id
                            ? 'bg-[#00A3E0] text-white shadow-sm'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                        }`}
                      >
                        {filterType.icon}
                        <span>{filterType.label}</span>
                        {/* Don't show count for 'All' when a filter is active */}
                        {!(filterType.id === 'all' && typeFilter !== 'all') && (
                          <span className={`${typeFilter === filterType.id ? 'text-white/80' : 'text-gray-500'}`}>
                            ({filterType.count})
                          </span>
                        )}
                      </button>
                    ))}
                  <button
                    onClick={() => setShowHelp(!showHelp)}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-full text-xs text-gray-500 hover:text-[#00A3E0] transition-colors ml-auto"
                    title={t({ en: 'Help', de: 'Hilfe' })}
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </div>

                {/* Help Tooltip */}
                {showHelp && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-gray-700">
                    <p className="font-medium mb-1">{t({ en: 'Quick filter shortcuts:', de: 'Schnellfilter-Shortcuts:' })}</p>
                    <p className="text-gray-600">
                      {t({
                        en: 'Type article:, video:, quiz:, playlist:, or category: before your search',
                        de: 'Tippe artikel:, video:, quiz:, wiedergabeliste: oder kategorie: vor deiner Suche'
                      })}
                    </p>
                    <p className="text-gray-500 mt-1 italic">
                      {t({ en: 'Example: article: live tracking', de: 'Beispiel: artikel: live tracking' })}
                    </p>
                  </div>
                )}

                <p className="text-sm text-gray-600 mt-2">
                  {typeFilter !== 'all' && (
                    <span className="inline-flex items-center gap-1 mr-2 text-[#00A3E0] font-medium">
                      {t({ en: 'Filtering:', de: 'Filterung:' })} {t({
                        en: typeFilter.charAt(0).toUpperCase() + typeFilter.slice(1),
                        de: typeFilter === 'videos' ? 'Videos' :
                            typeFilter === 'playlists' ? 'Wiedergabelisten' :
                            typeFilter === 'quizzes' ? 'Quiz' :
                            typeFilter === 'articles' ? 'Artikel' :
                            'Kategorien'
                      })}
                    </span>
                  )}
                  {t({
                    en: `${getTotalResults()} result${getTotalResults() !== 1 ? 's' : ''} for "${
                      searchResults.searchTerm
                    }"`,
                    de: `${getTotalResults()} Ergebnis${getTotalResults() !== 1 ? 'se' : ''} für "${
                      searchResults.searchTerm
                    }"`,
                  })}
                </p>
              </div>

              <div className="max-h-80 overflow-y-auto">
                {/* Categories */}
                {(typeFilter === 'all' || typeFilter === 'categories') && searchResults.categories.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-gray-25 border-b border-gray-100">
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                        {t({ en: "Categories", de: "Kategorien" })}
                      </h4>
                    </div>
                    {searchResults.categories.map((category) => (
                      <SearchResult
                        key={`category-${category.category_id}`}
                        item={category}
                        onClose={closeResults}
                      />
                    ))}
                  </div>
                )}

                {/* Playlists */}
                {(typeFilter === 'all' || typeFilter === 'playlists') && searchResults.playlists.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-gray-25 border-b border-gray-100">
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                        {t({ en: "Playlists", de: "Wiedergabelisten" })}
                      </h4>
                    </div>
                    {searchResults.playlists.map((playlist) => (
                      <SearchResult
                        key={`playlist-${playlist.playlist_id}`}
                        item={playlist}
                        onClose={closeResults}
                      />
                    ))}
                  </div>
                )}

                {/* Videos */}
                {(typeFilter === 'all' || typeFilter === 'videos') && searchResults.videos.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-gray-25 border-b border-gray-100">
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                        {t({ en: "Videos", de: "Videos" })}
                      </h4>
                    </div>
                    {searchResults.videos.map((video) => (
                      <SearchResult
                        key={`video-${video.video_id}`}
                        item={video}
                        onClose={closeResults}
                      />
                    ))}
                  </div>
                )}

                {/* Quizzes */}
                {(typeFilter === 'all' || typeFilter === 'quizzes') && searchResults.quizzes?.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-gray-25 border-b border-gray-100">
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                        {t({ en: "Quizzes", de: "Quiz" })}
                      </h4>
                    </div>
                    {searchResults.quizzes.map((quiz) => (
                      <SearchResult
                        key={`quiz-${quiz.quiz_id}`}
                        item={quiz}
                        onClose={closeResults}
                      />
                    ))}
                  </div>
                )}

                {/* Articles */}
                {(typeFilter === 'all' || typeFilter === 'articles') && searchResults.articles?.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-gray-25 border-b border-gray-100">
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                        {t({ en: "Articles", de: "Artikel" })}
                      </h4>
                    </div>
                    {searchResults.articles.map((article) => (
                      <SearchResult
                        key={`article-${article.id}`}
                        item={article}
                        onClose={closeResults}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* View All Button */}
              {getTotalResults() > 0 && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                  <button
                    onClick={handleViewAll}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#00A3E0] hover:bg-[#0092C9] text-white rounded-lg transition-colors font-medium text-sm"
                  >
                    <Search className="w-4 h-4" />
                    {typeFilter !== 'all' ? (
                      t({
                        en: `View All ${typeFilter.charAt(0).toUpperCase() + typeFilter.slice(1)} (${getTotalResults()})`,
                        de: `Alle ${
                          typeFilter === 'videos' ? 'Videos' :
                          typeFilter === 'playlists' ? 'Wiedergabelisten' :
                          typeFilter === 'quizzes' ? 'Quiz' :
                          typeFilter === 'articles' ? 'Artikel' :
                          'Kategorien'
                        } anzeigen (${getTotalResults()})`
                      })
                    ) : (
                      t({
                        en: `View All Results (${getTotalResults()})`,
                        de: `Alle Ergebnisse anzeigen (${getTotalResults()})`
                      })
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="px-4 py-8 text-center text-gray-500">
              <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">
                {t({
                  en: `No results found for "${searchResults.searchTerm}"`,
                  de: `Keine Ergebnisse für "${searchResults.searchTerm}" gefunden`,
                })}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
